import { FC, Fragment, MouseEvent, useEffect, useState } from 'react'

import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useGetTranslatedNames } from 'featureSliced/shared/hooks/useGetTranslatedNames'

import { Button } from 'featureSliced/entities/Button'
import { ProgressBar } from 'components/ProgressBar'
import { RenderIf } from 'components/RenderIf'

import { QRMBucketSlice } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer'

import { getProductDiscount } from 'featureSliced/shared/utils/getProductDiscount'
import {
  getPrecision,
  roundValueTo,
} from 'featureSliced/shared/utils/roundValueTo'
import { getDiscountedPrice } from 'featureSliced/shared/utils/getDiscountedPrice'
import { getItemUnits } from 'featureSliced/shared/utils/getItemUnits'
import { convertToPrice } from 'featureSliced/shared/utils/convertToPrice'

import {
  IMenuItemsBucket,
  ProductTemplateIdType,
} from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'
import {
  IMenuEntry,
  IProduct,
} from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import { useOpenProductInfoModal } from '../../../useOpenProductInfoModal'
import { useWebLayout } from 'featureSliced/shared/hooks/useWebLayout'
import { AllergenImages } from 'featureSliced/shared/utils/AllergenImages'
import { ProductInfoModal } from 'featureSliced/widgets/Modals/ProductInfoModal'

import { ReactComponent as CameraIcon } from 'assets/cameraIcon.svg'
import chevronDown from 'assets/chevronDown.svg'
import {
  ItemsContainer,
  ItemsGrid,
  ItemsGridExpander,
  MainTitle,
  Item,
  ItemImageContainer,
  ItemImage,
  ItemPrice,
  ItemInfoIcon,
  ItemInfoIconCorner,
  ItemTitleRow,
  ItemTitleText,
  ItemAllergens,
  ItemAllergen,
  ItemBody,
  ItemDetails,
} from './styled'

// a sub-article has something to show when the catalogue carries allergens or a
// note, or the product-info item exists; zero ids never reach /product-info
const hasSubArticleInfo = (item: IMenuEntry) =>
  item.categoryId > 0 &&
  item.id > 0 &&
  (item.hasProductInfo === true ||
    Boolean(item.allergens?.length) ||
    Boolean(item.note))
import { useGetProductPrice } from 'featureSliced/shared/hooks/useGetProductPrice/indexT'

type AddMenuItemsProps = {
  product: IProduct
  productTemplateId: ProductTemplateIdType
  closeModal: () => void
}

export const AddMenuItems: FC<AddMenuItemsProps> = ({
  product,
  closeModal,
  productTemplateId,
  ...selectedKitchenInfo
}) => {
  // Hooks
  const { getProductName } = useGetTranslatedNames()
  const dispatch = useMyDispatch()
  // The parent step's kitchen-info choice arrives once via propsNext (Modal's
  // step-to-step relay, components/Modal/index.tsx). Opening a stacked modal
  // from in here (the sub-article info sheet) changes the modal count, which
  // clears propsNext — so the choice must be captured on mount, not re-read
  // from props on every render.
  const [initialKitchenInfo] = useState(() => selectedKitchenInfo)
  const t = useTranslations()
  const { getProductPrice } = useGetProductPrice()
  const openProductInfo = useOpenProductInfoModal()
  const { isWeb } = useWebLayout()

  // Actions
  const addProductToBucket = QRMBucketSlice.actions.addProductToBucket

  // Store
  const Currency = useMySelector((state) => state.app.companyData.Currency)
  const discounts = useMySelector((state) => state.specials?.discounts)

  // v2: menus come directly from the product
  const menus = product.menus || []

  // State
  const [currentSelected, setCurrentSelected] = useState(0)
  const [selectedMenuItems, setSelectedMenuItems] = useState<
    IMenuItemsBucket[]
  >([])
  const [isItemsCollapsed, setIsItemsCollapsed] = useState<boolean>(false)
  const [isCollapsedTouched, setIsCollapsedTouched] = useState<boolean>(false)
  const [expandedDetailsId, setExpandedDetailsId] = useState<number | null>(
    null
  )

  // useEffects
  useEffect(() => {
    if (!isCollapsedTouched && menus[currentSelected]?.items?.length > 6) {
      setIsItemsCollapsed(true)
    }
  }, [menus, currentSelected])

  // Auto-add fixed items for the newly visible menu step
  useEffect(() => {
    const fixedItems =
      menus[currentSelected]?.items?.filter((item) => item.isFixed) || []
    if (!fixedItems.length) return
    setSelectedMenuItems((prev) => {
      const existingIds = new Set(
        prev
          .filter((el) => el.menuIndex === currentSelected)
          .map((el) => el.productId)
      )
      const toAdd = fixedItems.filter((item) => !existingIds.has(item.id))
      if (!toAdd.length) return prev
      return [
        ...prev,
        ...toAdd.map((item) => {
          const discountP = getProductDiscount(item as any, discounts)
          const finalPrice = item.price ?? 0
          return {
            productId: item.id,
            count: item.count ?? 1,
            menuIndex: currentSelected,
            price: finalPrice,
            name: item.title,
            kitchenInfo: item.kitchenInfo ?? [],
            imageUrl: item.imageUrl ?? undefined,
            categoryId: item.categoryId,
            isApplyDiscount: Boolean(discountP),
            isFixed: true,
            total: roundValueTo(
              getDiscountedPrice(finalPrice, discountP),
              getPrecision(Currency)
            ),
          } as unknown as IMenuItemsBucket
        }),
      ]
    })
  }, [currentSelected])

  useEffect(() => {
    const isMaxReached =
      selectedMenuItems.filter((el) => el.menuIndex === currentSelected)
        ?.length >= menus?.[currentSelected]?.max
    if (isMaxReached) {
      orderOrNext()
    }
  }, [selectedMenuItems])

  // Functions
  const addItemToSelected = (item: any, menuIndex: number) => {
    if (item.isFixed) return
    const discountP = getProductDiscount(item, discounts)
    const finalPrice = item.price ?? 0
    setSelectedMenuItems((prevValue) => [
      ...prevValue,
      {
        productId: item.id,
        count: 1,
        menuIndex,
        price: finalPrice,
        name: item.title,
        kitchenInfo: item.kitchenInfo ?? [],
        imageUrl: item.imageUrl ?? undefined,
        categoryId: item.categoryId,
        unitSellCode: item.unitSellCode,
        isApplyDiscount: Boolean(discountP),
        isFixed: false,
        total: roundValueTo(
          getDiscountedPrice(finalPrice, discountP),
          getPrecision(Currency)
        ),
      } as unknown as IMenuItemsBucket,
    ])
  }

  const orderOrNext = () => {
    if (currentSelected + 1 < menus.length) {
      setCurrentSelected((prevValue) => prevValue + 1)
    }
    if (currentSelected + 1 === menus.length) {
      orderProduct()
    }
  }

  const orderProduct = () => {
    const { id, categoryId, unitSellCode } = product
    const sKI = JSON.stringify(initialKitchenInfo)
    const sMI = selectedMenuItems
      ?.map((sMI) => sMI.productId)
      ?.sort((a, b) => a - b)
    const productToAdd = {
      uniqueId: `${productTemplateId}_${id}_${sKI}_${sMI}`,
      productId: id,
      productTemplateId,
      categoryId,
      count: 0, // 0 is needed for UPDATE to work correctly
      kitchenInfo: JSON.stringify(initialKitchenInfo || {}),
      price: getProductPrice(product, productTemplateId).finalPrice,
      addedAsWeight: unitSellCode === 2 || unitSellCode === 3,
      menuItems: selectedMenuItems,
    }
    dispatch(addProductToBucket(productToAdd))
    closeModal()
  }

  const getSlicedArray = <T,>(arrayToSlice: T[]): T[] => {
    if (!isCollapsedTouched && isItemsCollapsed) {
      return arrayToSlice.slice(0, 6)
    }
    return arrayToSlice
  }

  const currentMenu = menus[currentSelected]
  const minCount = currentMenu?.min || 0
  const maxCount = currentMenu?.max || 1

  return (
    <div style={{ marginTop: '35px' }}>
      <RenderIf condition={menus.length > 1}>
        <ProgressBar progress={((currentSelected + 1) / menus.length) * 100} />
      </RenderIf>
      <MainTitle>
        {minCount === maxCount
          ? t('products.buildPerfectSpecific', {
              productName: getProductName(product),
              count: minCount || 1,
            })
          : t('products.buildPerfect', {
              productName: getProductName(product),
              minCount,
              maxCount,
            })}
      </MainTitle>
      <ItemsContainer>
        <ItemsGrid>
          {getSlicedArray(currentMenu?.items || []).map((item) => {
            const isFixed = Boolean(item.isFixed)
            return (
              <Fragment key={`menu-item_${item.id}`}>
                <Item
                  onClick={() => addItemToSelected(item, currentSelected)}
                  isSelected={
                    isFixed ||
                    Boolean(
                      selectedMenuItems?.find(
                        (selectedItem) => selectedItem.productId === item.id
                      )
                    )
                  }
                  count={
                    isFixed
                      ? item.count ?? 1
                      : getItemUnits(item.id, selectedMenuItems)
                  }
                  style={
                    isFixed ? { cursor: 'default', opacity: 0.85 } : undefined
                  }
                >
                  <RenderIf condition={!isWeb && hasSubArticleInfo(item)}>
                    <ItemInfoIconCorner
                      type="button"
                      aria-label={t('productInfo.open')}
                      data-testid={`menu_item_info_${item.id}`}
                      onClick={(e: MouseEvent) => {
                        e.stopPropagation()
                        openProductInfo(item, { withCatalogueSections: true })
                      }}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.6"
                        strokeLinecap="round"
                        aria-hidden="true"
                      >
                        <path d="M12 11v6" />
                        <path d="M12 7.5h.01" />
                      </svg>
                    </ItemInfoIconCorner>
                  </RenderIf>
                  <ItemImageContainer>
                    <RenderIf condition={!!item.imageUrl}>
                      <ItemImage src={item.imageUrl} />
                    </RenderIf>
                    <RenderIf condition={!item.imageUrl}>
                      <CameraIcon />
                    </RenderIf>
                  </ItemImageContainer>
                  <ItemBody>
                    <ItemTitleRow>
                      <ItemTitleText>{getProductName(item)}</ItemTitleText>
                      <RenderIf condition={isWeb && hasSubArticleInfo(item)}>
                        <ItemInfoIcon
                          type="button"
                          aria-label={t('productInfo.open')}
                          data-testid={`menu_item_details_${item.id}`}
                          aria-expanded={expandedDetailsId === item.id}
                          onClick={(e: MouseEvent) => {
                            e.stopPropagation()
                            setExpandedDetailsId((prev) =>
                              prev === item.id ? null : item.id
                            )
                          }}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.6"
                            strokeLinecap="round"
                            aria-hidden="true"
                          >
                            <path d="M12 11v6" />
                            <path d="M12 7.5h.01" />
                          </svg>
                        </ItemInfoIcon>
                      </RenderIf>
                    </ItemTitleRow>
                    <RenderIf condition={!!item.allergens?.length}>
                      <ItemAllergens
                        data-testid={`menu_item_allergens_${item.id}`}
                      >
                        {item.allergens?.map((allergen) => (
                          <ItemAllergen
                            key={`menu-item-allergen_${item.id}_${allergen.id}`}
                            alt={String(allergen.id)}
                            src={
                              AllergenImages[allergen.id] || AllergenImages[0]
                            }
                          />
                        ))}
                      </ItemAllergens>
                    </RenderIf>
                  </ItemBody>
                  <ItemPrice>
                    {item.price
                      ? `${convertToPrice(item.price)} ${Currency || ''}`
                      : ''}
                  </ItemPrice>
                </Item>
                {/* a SIBLING of Item, not a child — kept out of the reach
                  of Item's own `p` rule (dark grey, forced size, meant for
                  the tile's title/price), which would otherwise leak into
                  the info body this hosts */}
                <RenderIf condition={isWeb && expandedDetailsId === item.id}>
                  <ItemDetails>
                    <ProductInfoModal
                      categoryId={item.categoryId}
                      productId={item.id}
                      fetchDetails={item.hasProductInfo === true}
                      withCatalogueSections
                      allergens={item.allergens}
                      allergenLabels={item.allergenLabels}
                      note={item.note}
                    />
                  </ItemDetails>
                </RenderIf>
              </Fragment>
            )
          })}
        </ItemsGrid>
        <RenderIf condition={isItemsCollapsed}>
          <ItemsGridExpander
            onClick={() => {
              setIsItemsCollapsed(false)
              setIsCollapsedTouched(true)
            }}
          >
            <img alt="Show all" src={chevronDown} />
          </ItemsGridExpander>
        </RenderIf>
      </ItemsContainer>
      <Button
        width="100%"
        buttonType="common"
        onClick={orderOrNext}
        disabled={
          selectedMenuItems.filter((el) => el.menuIndex === currentSelected)
            ?.length < minCount
        }
      >
        {currentSelected + 1 === menus.length
          ? t('buttons.accept')
          : t('common.next')}
      </Button>
    </div>
  )
}
